import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { leaveService } from '@/services/leave/leaveService';
import { leaveQueryKeys } from '@/hooks/leave/queryKeys';
import type { LeaveApprovalFilters } from '@/types/leaveApprovalFilters';
import type { LeaveRequestWithProfile } from '@/services/leave/types';
import { 
  DEFAULT_LEAVE_APPROVAL_FILTERS, 
  countActiveFilters,
  SPECIAL_MONTH_VALUES,
  getCurrentMonth,
} from '@/types/leaveApprovalFilters';

const PAGE_SIZE = 50;

// URL param keys
const URL_PARAMS = {
  month: 'month',
  employees: 'employees',
  types: 'types',
  statuses: 'statuses',
} as const;

// Parse filters from URL (default to current month if no month param)
const parseFiltersFromURL = (searchParams: URLSearchParams): LeaveApprovalFilters => {
  const month = searchParams.get(URL_PARAMS.month) || getCurrentMonth();
  const employeesParam = searchParams.get(URL_PARAMS.employees);
  const typesParam = searchParams.get(URL_PARAMS.types);
  const statusesParam = searchParams.get(URL_PARAMS.statuses);

  return {
    month,
    employeeIds: employeesParam ? employeesParam.split(',').filter(Boolean) : [],
    leaveTypes: typesParam ? typesParam.split(',').filter(Boolean) : [],
    statuses: statusesParam ? statusesParam.split(',').filter(Boolean) : [],
  };
};

// Serialize filters to URL params
const serializeFiltersToURL = (filters: LeaveApprovalFilters): Record<string, string> => {
  const params: Record<string, string> = {};
  
  if (filters.month) {
    params[URL_PARAMS.month] = filters.month;
  }
  
  if (filters.employeeIds.length > 0) {
    params[URL_PARAMS.employees] = filters.employeeIds.join(',');
  }
  
  if (filters.leaveTypes.length > 0) {
    params[URL_PARAMS.types] = filters.leaveTypes.join(',');
  }
  
  if (filters.statuses.length > 0) {
    params[URL_PARAMS.statuses] = filters.statuses.join(',');
  }
  
  return params;
};

export const useLeaveApprovalFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Parse initial filters from URL
  const [filters, setFilters] = useState<LeaveApprovalFilters>(() => 
    parseFiltersFromURL(searchParams)
  );
  
  // Applied filters (for query) - only updates on "Apply"
  const [appliedFilters, setAppliedFilters] = useState<LeaveApprovalFilters>(() => 
    parseFiltersFromURL(searchParams)
  );

  // Pagination state
  const [page, setPage] = useState(0);
  const [accumulatedRequests, setAccumulatedRequests] = useState<LeaveRequestWithProfile[]>([]);

  // Debounced URL sync
  const debouncedUpdateURL = useDebouncedCallback((newFilters: LeaveApprovalFilters) => {
    const params = serializeFiltersToURL(newFilters);
    const newSearchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      newSearchParams.set(key, value);
    });
    
    setSearchParams(newSearchParams, { replace: true });
  }, 300);

  // Update individual filter value (staging area)
  const updateFilter = useCallback(<K extends keyof LeaveApprovalFilters>(
    key: K,
    value: LeaveApprovalFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Apply filters (triggers data fetch and URL update) — resets pagination
  const applyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setPage(0);
    setAccumulatedRequests([]);
    debouncedUpdateURL(filters);
  }, [filters, debouncedUpdateURL]);

  // Clear all filters (reset to default: current month)
  const clearFilters = useCallback(() => {
    const defaultFilters: LeaveApprovalFilters = {
      ...DEFAULT_LEAVE_APPROVAL_FILTERS,
      month: getCurrentMonth(),
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(0);
    setAccumulatedRequests([]);
    debouncedUpdateURL(defaultFilters);
  }, [debouncedUpdateURL]);

  // Check if filters have pending changes
  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  }, [filters, appliedFilters]);

  // Active filter count
  const activeFilterCount = useMemo(() => 
    countActiveFilters(appliedFilters), [appliedFilters]
  );

  // Query for filtered leave requests with pagination
  const {
    data: queryResult,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      ...leaveQueryKeys.companyRequests(),
      'filtered',
      appliedFilters.month,
      appliedFilters.employeeIds,
      appliedFilters.leaveTypes,
      appliedFilters.statuses,
      page,
    ],
    queryFn: () => leaveService.getFilteredCompanyLeaveRequests({
      ...appliedFilters,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    staleTime: 2 * 60 * 1000,
  });

  // Accumulate results when new page data arrives
  useEffect(() => {
    if (queryResult?.data) {
      if (page === 0) {
        setAccumulatedRequests(queryResult.data);
      } else {
        setAccumulatedRequests(prev => {
          const existingIds = new Set(prev.map(r => r.id));
          const newItems = queryResult.data.filter(r => !existingIds.has(r.id));
          return [...prev, ...newItems];
        });
      }
    }
  }, [queryResult, page]);

  // Load more handler
  const loadMore = useCallback(() => {
    setPage(prev => prev + 1);
  }, []);

  // Reset accumulation on mutation invalidation (refetch)
  const resetAndRefetch = useCallback(() => {
    setPage(0);
    setAccumulatedRequests([]);
    refetch();
  }, [refetch]);

  // Extract unique leave types from accumulated data for filter options
  const availableLeaveTypes = useMemo(() => {
    const types = new Set<string>();
    accumulatedRequests.forEach(req => {
      if (req.leave_type) types.add(req.leave_type);
    });
    return Array.from(types).sort();
  }, [accumulatedRequests]);

  return {
    // Current (staged) filter values
    filters,
    updateFilter,
    
    // Applied filters (used for query)
    appliedFilters,
    applyFilters,
    clearFilters,
    
    // State
    hasPendingChanges,
    activeFilterCount,
    
    // Query results (accumulated for Load More)
    leaveRequests: accumulatedRequests,
    totalCount: queryResult?.totalCount ?? 0,
    hasMore: queryResult?.hasMore ?? false,
    isLoading,
    error,
    refetch: resetAndRefetch,
    loadMore,
    
    // Derived data for filter options
    availableLeaveTypes,
  };
};
